import { ChangeEvent } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import IconButton from '~/components/buttons/NewIconButton';
import { SkeletonLoader } from '~/components/data-display/SkeletonLoader/SkeletonLoader';
import DeleteIcon from '~/icons/icon-delete.svg?react';
import { useModal } from '~/modals/Modal';
import { useAllBookkeepingAccountsSuspenseQuery } from '~/services/bookkeeping/bookkeeping-accounts';
import { useBookkeepingConfiguration } from '~/services/bookkeeping/bookkeeping-configuration';
import { useFlowQuery, useSplitPayment } from '~/services/flow';
import { useAdjustmentAccountMemory } from '~/state/bookkeeping/adjustmentAccounts';
import {
  useShowAdjustmentColumn,
  useShowCompetencePeriodColumn,
  useShowDescriptionColumn,
} from '~/state/bookkeeping/columnVisibility';
import { reconcileTransactionsFiltersStorageKey } from '~/state/modals/reconcileTransactionModal';
import { patchStoredFilters } from '~/state/tableFilters';
import { Counterpart } from '~/types/counterparts';
import { DateRange } from '~/types/date';
import { Document, DocumentDirection } from '~/types/document';
import { BookkeepingAccount, BookkeepingAccountType, BookkeepingEntrySource } from '~/types/general-bookkeeping';
import { Money } from '~/types/money';
import { Transaction } from '~/types/transaction';
import { assertIsDefined } from '~/types/typeGuards';
import { deriveAdjustmentType, requiresAdjustment } from '~/utils/bookkeeping/adjustments';
import { isPaidInDare } from '~/utils/bookkeeping/bookkeeping-entry';
import { absMoney, ensureDecimalPlaces } from '~/utils/money';
import PurchasePeriodTableField from '~/views/bookkeeping/VAT-registration/PurchasePeriodTableField';
import AmountInput from '~/views/bookkeeping/components/AmountInput/AmountInput';
import tableStyles from '~/views/bookkeeping/registration-table.module.scss';
import AssociatedDocumentCell from '~/views/bookkeeping/transactions/AssociatedDocumentCell';
import BookkeepingDocumentSelectionDialog from '~/views/bookkeeping/transactions/BookkeepingDocumentSelectionDialog/BookkeepingDocumentSelectionDialog';
import {
  ConfirmReconciliationParams,
  ReconcileTransactionModal,
} from '~/views/transactions/aside/reconciliation/ReconcileTransactionModal';
import AdjustmentAccountCell from './AdjustmentAccountCell';
import BookkeepingAccountField from './BookkeepingAccountField';
import BookkeepingCounterpartField from './BookkeepingCounterpartField';
import styles from './BookkeepingTransactionRow.module.scss';
import type { BookkeepingTransactionFormInput } from './BookkeepingTransactionSchema';

export interface AddRowProps {
  dare?: Money;
  avere?: Money;
  bookkeepingAccountId?: string;
}

interface Props {
  autoFocus?: 'amount' | 'account' | null;
  index: number;
  fieldId: string;
  onAddRow: (props?: AddRowProps) => number;
  onDelete: ((index: number) => void) | null;
  onShowAdjustmentColumn: () => void;
  transaction: Transaction | null;
  accountSuggestionIsLoading?: boolean;
}

const BookkeepingTransactionRow: React.FC<Props> = ({
  autoFocus,
  index,
  fieldId,
  onAddRow,
  onDelete,
  onShowAdjustmentColumn,
  transaction,
  accountSuggestionIsLoading,
}) => {
  const { control, setValue, getValues } = useFormContext<BookkeepingTransactionFormInput>();
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'transactions.create.form' });
  const { showModal } = useModal();
  const configuration = useBookkeepingConfiguration();
  const bookkeepingAccounts = configuration.accounts;
  const [showDescriptionColumn] = useShowDescriptionColumn();
  const [showCompetencePeriodColumn] = useShowCompetencePeriodColumn();
  const [showAdjustmentColumn] = useShowAdjustmentColumn();
  const { recall, save } = useAdjustmentAccountMemory();

  const { data: accountsMap } = useAllBookkeepingAccountsSuspenseQuery({ hasChildren: false });
  const bookkeepingAccountId = useWatch({ control, name: `entries.${index}.bookkeepingAccountId` });
  const isSPAccount = accountsMap[bookkeepingAccountId]?.accountType === BookkeepingAccountType.SP;
  const doSplitPayment = useSplitPayment();

  const readOnlyAmount = useWatch({ control, name: `entries.${index}.readOnlyAmount` });
  const readOnlyAccount = useWatch({ control, name: `entries.${index}.readOnlyAccount` });
  const documentId = useWatch({ control, name: `entries.${index}.documentId` });
  const documentNumber = useWatch({ control, name: `entries.${index}.documentNumber` });
  const documentType = useWatch({ control, name: `entries.${index}.documentType` });
  const counterpart = useWatch({ control, name: `entries.${index}.counterpart` });
  const registrationDate = useWatch({ control, name: 'date' });
  const competencePeriodStart = useWatch({ control, name: `entries.${index}.competencePeriodStart` });
  const endDate = useWatch({ control, name: `entries.${index}.competencePeriodEnd` });
  const adjustmentAccountId = useWatch({ control, name: `entries.${index}.adjustmentAccountId` });

  const needsAdjustment = requiresAdjustment(registrationDate, competencePeriodStart, endDate);

  const recommendedReconciliationFlowId = useWatch({ control, name: `recommendedReconciliationFlowId` });
  const { data: recommendedReconciliationFlow } = useFlowQuery(recommendedReconciliationFlowId ?? null);
  const recommendedReconciliationDocumentId = recommendedReconciliationFlow?.document.id;

  const handleAccountChange = (account: BookkeepingAccount | null) => {
    setValue(`entries.${index}.bookkeepingAccountId`, account?.id ?? '', { shouldValidate: true, shouldDirty: true });
    setValue(`entries.${index}.source`, BookkeepingEntrySource.USER);
    if (account?.accountType === BookkeepingAccountType.CE) {
      setValue(`entries.${index}.competencePeriodStart`, null, { shouldDirty: true });
      setValue(`entries.${index}.competencePeriodEnd`, null, { shouldDirty: true });
    }
  };

  const handleCounterpartChange = (counterpart: Counterpart | null) => {
    setValue(`entries.${index}.counterpart`, counterpart ?? null, { shouldValidate: true, shouldDirty: true });
    setValue(`entries.${index}.source`, BookkeepingEntrySource.USER);
  };

  const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(`entries.${index}.description`, event.target.value, { shouldValidate: true, shouldDirty: true });
    setValue(`entries.${index}.source`, BookkeepingEntrySource.USER);
  };

  const syncAdjustmentAccount = (
    newStart: Date | null,
    newEnd: Date | null,
    newIsExpense: boolean,
    oldType: ReturnType<typeof deriveAdjustmentType>,
  ) => {
    if (!requiresAdjustment(registrationDate, newStart, newEnd)) {
      setValue(`entries.${index}.adjustmentAccountId`, null, { shouldDirty: true });
      return;
    }
    const newType = deriveAdjustmentType(registrationDate, newStart!, newEnd!, newIsExpense);
    if (newType !== oldType) {
      setValue(`entries.${index}.adjustmentAccountId`, newType ? (recall(newType) ?? null) : null, {
        shouldDirty: true,
      });
    }
  };

  const handleCompetencePeriodChange = (value: DateRange) => {
    const newStart = value.startDate ?? null;
    const newEnd = value.endDate ?? null;
    const isExpense = getValues(`entries.${index}.dare`) != null;
    const oldType =
      competencePeriodStart && endDate
        ? deriveAdjustmentType(registrationDate, competencePeriodStart, endDate, isExpense)
        : null;
    setValue(`entries.${index}.competencePeriodStart`, newStart, { shouldDirty: true });
    setValue(`entries.${index}.competencePeriodEnd`, newEnd, { shouldDirty: true });
    setValue(`entries.${index}.source`, BookkeepingEntrySource.USER);
    if (requiresAdjustment(registrationDate, newStart, newEnd)) onShowAdjustmentColumn();
    syncAdjustmentAccount(newStart, newEnd, isExpense, oldType);
  };

  const handleDocumentSelect = (document: Document, overrideIndex?: number, overrideAmount?: Money) => {
    const idx = overrideIndex ?? index;

    setValue(`entries.${idx}.flowId`, null, { shouldValidate: true, shouldDirty: true });
    setValue(`entries.${idx}.documentNumber`, document.number ?? '', { shouldValidate: true, shouldDirty: true });
    setValue(`entries.${idx}.documentId`, document.id, { shouldValidate: true, shouldDirty: true });
    setValue(`entries.${idx}.documentType`, document.documentType, { shouldValidate: true, shouldDirty: true });

    setValue(`entries.${idx}.counterpart`, document.counterpart ?? null, { shouldValidate: true, shouldDirty: true });

    setValue(`entries.${idx}.source`, BookkeepingEntrySource.USER);

    if (document.direction === DocumentDirection.issued && bookkeepingAccounts.creditsClients) {
      setValue(`entries.${idx}.bookkeepingAccountId`, bookkeepingAccounts.creditsClients, {
        shouldValidate: true,
      });
    } else if (document.direction === DocumentDirection.received && bookkeepingAccounts.debtsSuppliers) {
      setValue(`entries.${idx}.bookkeepingAccountId`, bookkeepingAccounts.debtsSuppliers, {
        shouldValidate: true,
      });
    }

    const amount = overrideAmount ?? document.grossAmount;
    if (!transaction || amount.currency === transaction.amount.currency) {
      if (isPaidInDare(document)) {
        setValue(`entries.${idx}.dare`, ensureDecimalPlaces(amount, 2), {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`entries.${idx}.avere`, null, { shouldValidate: true, shouldDirty: true });
      } else {
        setValue(`entries.${idx}.avere`, ensureDecimalPlaces(amount, 2), {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`entries.${idx}.dare`, null, { shouldValidate: true, shouldDirty: true });
      }
    }
  };

  const handleFlowSelect = async (params: ConfirmReconciliationParams) => {
    for (let i = 0; i < params.selectedFlows.length; i++) {
      const selectedFlow = params.selectedFlows[i]!;
      const flow = selectedFlow.flow;

      if (selectedFlow.split) {
        assertIsDefined(selectedFlow.remainingAmount, 'Remaining amount is not defined');
        await doSplitPayment.mutateAsync({
          sourceFlowId: flow.id,
          actionType: 'new-flow',
          data: {
            newFlowAmount: selectedFlow.remainingAmount,
          },
        });
      }

      const flowAlreadyAssigned = getValues('entries').some((entry) => entry.flowId === flow.id);
      if (!flowAlreadyAssigned) {
        const rowIndex = i == 0 ? index : onAddRow();
        handleDocumentSelect(flow.document, rowIndex, absMoney(selectedFlow.amount));
        setValue(`entries.${rowIndex}.flowId`, flow.id, { shouldValidate: true, shouldDirty: true });
      }
    }
    params.closeModal();
  };

  const handleDocumentClick = async () => {
    if (!transaction) {
      await showModal({
        component: BookkeepingDocumentSelectionDialog,
        props: {
          transaction,
          selectedCounterpart: counterpart,
          onDocumentSelect: handleDocumentSelect,
        },
      });
    } else {
      if (counterpart) {
        patchStoredFilters(reconcileTransactionsFiltersStorageKey, { counterpartId: counterpart.id });
      }
      await showModal({
        component: ReconcileTransactionModal,
        props: {
          transaction,
          onConfirm: handleFlowSelect,
        },
      });
    }
  };

  const handleRemoveDocumentClick = () => {
    if (getValues(`entries.${index}.documentId`)) {
      setValue(`entries.${index}.counterpart`, null, { shouldValidate: true, shouldDirty: true });
    }
    setValue(`entries.${index}.flowId`, null, { shouldValidate: true, shouldDirty: true });
    setValue(`entries.${index}.documentId`, null, { shouldValidate: true, shouldDirty: true });
    setValue(`entries.${index}.documentNumber`, null, { shouldValidate: true, shouldDirty: true });
    setValue(`entries.${index}.documentType`, null, { shouldValidate: true, shouldDirty: true });
    setValue(`entries.${index}.source`, BookkeepingEntrySource.USER);
  };

  const handleDareChange = (value: string) => {
    const oldIsExpense = getValues(`entries.${index}.dare`) != null;
    const oldType =
      competencePeriodStart && endDate
        ? deriveAdjustmentType(registrationDate, competencePeriodStart, endDate, oldIsExpense)
        : null;
    if (value) {
      setValue(
        `entries.${index}.dare`,
        { amount: value, currency: 'EUR' },
        { shouldValidate: true, shouldDirty: true },
      );
      setValue(`entries.${index}.avere`, null, { shouldValidate: true, shouldDirty: true });
    } else {
      setValue(`entries.${index}.dare`, null, { shouldValidate: true, shouldDirty: true });
    }
    setValue(`entries.${index}.source`, BookkeepingEntrySource.USER);
    syncAdjustmentAccount(competencePeriodStart, endDate, !!value, oldType);
  };

  const handleAvereChange = (value: string) => {
    const oldIsExpense = getValues(`entries.${index}.dare`) != null;
    const oldType =
      competencePeriodStart && endDate
        ? deriveAdjustmentType(registrationDate, competencePeriodStart, endDate, oldIsExpense)
        : null;
    if (value) {
      setValue(
        `entries.${index}.avere`,
        { amount: value, currency: 'EUR' },
        { shouldValidate: true, shouldDirty: true },
      );
      setValue(`entries.${index}.dare`, null, { shouldValidate: true, shouldDirty: true });
    } else {
      setValue(`entries.${index}.avere`, null, { shouldValidate: true, shouldDirty: true });
    }
    setValue(`entries.${index}.source`, BookkeepingEntrySource.USER);
    syncAdjustmentAccount(competencePeriodStart, endDate, false, oldType);
  };

  return (
    <tr key={fieldId}>
      <td className={styles.account}>
        {accountSuggestionIsLoading ? (
          <SkeletonLoader height={36} />
        ) : (
          <Controller
            control={control}
            name={`entries.${index}.bookkeepingAccountId`}
            render={({ field, fieldState: { invalid } }) => (
              <BookkeepingAccountField
                autoFocus={autoFocus === 'account'}
                invalid={invalid}
                readOnly={readOnlyAccount}
                value={field.value}
                onChange={handleAccountChange}
              />
            )}
          />
        )}
      </td>
      <td className={tableStyles.amountColumn}>
        <Controller
          control={control}
          name={`entries.${index}.dare`}
          render={({ field, fieldState: { invalid } }) => (
            <AmountInput
              {...field}
              ariaLabel={t('dare')}
              autoFocus={autoFocus === 'amount'}
              className={tableStyles.amount}
              isDisabled={readOnlyAmount}
              isInvalid={invalid}
              isReadOnly={readOnlyAmount}
              onChange={handleDareChange}
              value={field.value?.amount ?? ''}
            />
          )}
        />
      </td>
      <td className={tableStyles.amountColumn}>
        <Controller
          control={control}
          name={`entries.${index}.avere`}
          render={({ field, fieldState: { invalid } }) => (
            <AmountInput
              {...field}
              ariaLabel={t('avere')}
              className={tableStyles.amount}
              isDisabled={readOnlyAmount}
              isInvalid={invalid}
              isReadOnly={readOnlyAmount}
              onChange={handleAvereChange}
              value={field.value?.amount ?? ''}
            />
          )}
        />
      </td>
      <td className={styles.counterpartColumn}>
        <Controller
          control={control}
          name={`entries.${index}.counterpart`}
          render={({ field, fieldState: { invalid } }) => (
            <BookkeepingCounterpartField
              invalid={invalid}
              readOnly={!!documentId || readOnlyAmount}
              value={field.value}
              onChange={handleCounterpartChange}
            />
          )}
        />
      </td>
      <td>
        <AssociatedDocumentCell
          documentId={documentId}
          documentNumber={documentNumber}
          documentType={documentType}
          readOnly={readOnlyAmount}
          onRemoveDocumentClick={handleRemoveDocumentClick}
          onDocumentClick={handleDocumentClick}
          isARecommendedReconciliation={documentId === recommendedReconciliationDocumentId}
        />
      </td>
      {showDescriptionColumn && (
        <td>
          <Controller
            control={control}
            name={`entries.${index}.description`}
            render={({ field, fieldState: { invalid } }) => (
              <input
                {...field}
                data-invalid={invalid}
                type="text"
                placeholder={t('description_placeholder')}
                aria-label={t('description')}
                onChange={handleDescriptionChange}
              />
            )}
          />
        </td>
      )}
      {showCompetencePeriodColumn && (
        <td>
          <Controller
            control={control}
            name={`entries.${index}.competencePeriodStart`}
            render={({ field, fieldState: { invalid } }) => (
              <PurchasePeriodTableField
                {...field}
                data-invalid={invalid}
                value={field.value && endDate ? { startDate: field.value, endDate } : null}
                aria-label={t('competence_period')}
                isDisabled={isSPAccount}
                onChange={handleCompetencePeriodChange}
              />
            )}
          />
        </td>
      )}
      {showAdjustmentColumn && (
        <td className={styles.adjustmentCell}>
          <AdjustmentAccountCell
            disabled={!needsAdjustment}
            value={adjustmentAccountId}
            onChange={(accountId) => {
              setValue(`entries.${index}.adjustmentAccountId`, accountId, { shouldDirty: true });
              if (accountId && competencePeriodStart && endDate) {
                const dare = getValues(`entries.${index}.dare`);
                const type = deriveAdjustmentType(registrationDate, competencePeriodStart, endDate, dare != null);
                if (type) save(type, accountId);
              }
            }}
          />
        </td>
      )}
      {onDelete && (
        <td>{!readOnlyAmount && <IconButton variant="ghost" Icon={DeleteIcon} onClick={() => onDelete(index)} />}</td>
      )}
    </tr>
  );
};

export default BookkeepingTransactionRow;
